import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import {
  type FilePartsFragment,
  type PicturePartsFragment,
} from '~/apollo/generated/v4/graphql';
import type { TargetBookmarksManagerModalProps } from '~/components/bookmark/TargetBookmarksManagerModal';
import { TargetBookmarksManagerModal } from '~/components/bookmark/TargetBookmarksManagerModal';
import { Modal } from '~/components/common/Modal';
import { PublishedContainer } from '~/components/common/published-container';
import { Button } from '~/components/ui/button';
import { SODetailText } from '~/components/upload/supporting-object/so-detail-text';
import { useRefetchQueriesV4 } from '~/hooks/apollo';
import { useModalState } from '~/hooks/modal';
import { cn, truncateText } from '~/utils/common';
import type { RefetchQueries } from '~/utils/graphql';
import { isPublished } from '~/utils/modules/supporting-object';

type Picture = PicturePartsFragment & {
  file: FilePartsFragment;
};

type Props = {
  picture: Picture;
  imgSrc: string;
  thumbnailImgSrc?: string;
  fade?: boolean;
  containerClassName?: string;
  bookmarkable?: TargetBookmarksManagerModalProps;
  showCaption?: boolean;
  refetchQueries: RefetchQueries;
  aspect?: 'square' | 'auto';
};

export function PictureThumbnailV4({
  picture,
  imgSrc,
  thumbnailImgSrc,
  fade = false,
  containerClassName,
  bookmarkable,
  showCaption,
  refetchQueries,
  aspect = 'auto',
}: Props) {
  const { show, showModal, hideModal } = useModalState();
  const [shouldOpenInNewWindow, setShouldOpenInNewWindow] = useState(false);
  const [refetch] = useRefetchQueriesV4(refetchQueries);
  const imgSrcRef = useRef(imgSrc);

  useEffect(() => {
    if (shouldOpenInNewWindow && imgSrcRef.current !== imgSrc) {
      setShouldOpenInNewWindow(false);
      window.open(imgSrc, '_blank');
    }
    imgSrcRef.current = imgSrc;
  }, [imgSrc, shouldOpenInNewWindow]);

  async function reloadAndOpenInNewWindow() {
    setShouldOpenInNewWindow(true);
    await refetch();
  }

  return (
    <>
      <div className={containerClassName}>
        <PublishedContainer published={isPublished(picture)} withIcon>
          <a
            href="#enlarge-image"
            onClick={showModal}
            className={cn('hover:border-2 hover:border-primary', {
              'w-full aspect-square relative overflow-hidden':
                aspect === 'square',
              'fade-img': fade,
            })}
          >
            <img
              src={thumbnailImgSrc ?? imgSrc}
              alt={picture.name}
              className={cn({
                'absolute inset-0 w-full h-full object-cover':
                  aspect === 'square',
              })}
            />
          </a>

          {picture && picture.description && showCaption && (
            <div className="caption text-center">
              {truncateText(picture.description, 50)}
            </div>
          )}
        </PublishedContainer>
      </div>

      <Modal open={show} onHide={hideModal} size="lg">
        <Modal.Body heading={picture.name ?? 'Picture'}>
          {bookmarkable && (
            <div className="text-right">
              <TargetBookmarksManagerModal {...bookmarkable} />
            </div>
          )}

          <div className="w-full space-y-4">
            <div className="text-center space-y-0.5">
              <img
                src={imgSrc}
                alt={picture.name}
                className="mx-auto inline max-w-full"
              />
              <div className="text-right">
                <Button
                  type="button"
                  onClick={reloadAndOpenInNewWindow}
                  color="ghost"
                  size="sm"
                  endIcon={<FontAwesomeIcon icon={faExternalLink} />}
                  loading={shouldOpenInNewWindow}
                >
                  View in full resolution
                </Button>
              </div>
            </div>

            {picture && (
              <div>
                <SODetailText label="Description" value={picture.description} />
                <SODetailText label="Author" value={picture.author} />
                <SODetailText label="Type" value={picture.type} />
                <SODetailText label="Scale" value={picture.scale} />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
